import { DOCUMENT_REVIEW_STATUSES, DOCUMENT_VALIDITY_STATUSES } from './constant';
import { DEFAULT_ACCEPTED_DOCUMENTS, isSupportingDocument } from './accepted-documents';
import { getDocumentDaysUntilExpiry } from './document-date';

const expiringSoonDays = 30;

export const primaryDocumentTypes = DEFAULT_ACCEPTED_DOCUMENTS.map((document) => document.type);

export const getDocumentValidity = (document, now = Date.now()) => {
  // Explicit API validity takes precedence over the legacy expiry date.
  if (document.validityStatus) {
    return document.validityStatus;
  }
  const daysUntilExpiry = Number.isInteger(document.daysUntilExpiry)
    ? document.daysUntilExpiry : getDocumentDaysUntilExpiry(document.expiryDate, now);
  if (!Number.isFinite(daysUntilExpiry) || daysUntilExpiry < 0) {
    return DOCUMENT_VALIDITY_STATUSES.expired;
  }
  return daysUntilExpiry <= expiringSoonDays ? DOCUMENT_VALIDITY_STATUSES.expiringSoon : DOCUMENT_VALIDITY_STATUSES.valid;
};

export const isDocumentCurrent = (document) => [DOCUMENT_VALIDITY_STATUSES.valid, DOCUMENT_VALIDITY_STATUSES.expiringSoon].includes(getDocumentValidity(document));
export const isDocumentApproved = (document) => !document.reviewStatus || document.reviewStatus === DOCUMENT_REVIEW_STATUSES.approved;
export const isDocumentEligible = (document) => isDocumentCurrent(document) && isDocumentApproved(document);
export const getPrimaryDocumentStatus = (documents = [], acceptedTypes = primaryDocumentTypes) => {
  const catalogue = acceptedTypes.map((entry) => typeof entry === 'string' ? { type: entry } : entry);
  const accepted = catalogue.filter((entry) => entry.isSupporting !== true);
  const primaryDocuments = (documents || []).filter((document) => accepted.some((entry) => entry.type === document.type));
  return {
    hasPrimary: primaryDocuments.length > 0,
    hasSupporting: (documents || []).some((document) => isSupportingDocument(document.type, catalogue)),
    document: primaryDocuments.find(isDocumentEligible),
    expiringSoon: primaryDocuments.some((document) => getDocumentValidity(document) === DOCUMENT_VALIDITY_STATUSES.expiringSoon),
    eligible: primaryDocuments.some(isDocumentEligible),
    allExpired: primaryDocuments.length > 0 && primaryDocuments.every((document) => getDocumentValidity(document) === DOCUMENT_VALIDITY_STATUSES.expired),
    pending: primaryDocuments.find((document) => document.reviewStatus === DOCUMENT_REVIEW_STATUSES.pending && isDocumentCurrent(document)),
    rejected: primaryDocuments.find((document) => document.reviewStatus === DOCUMENT_REVIEW_STATUSES.rejected),
    expired: primaryDocuments.find((document) => getDocumentValidity(document) === DOCUMENT_VALIDITY_STATUSES.expired),
  };
};

export const getServiceDocumentStatus = (documents = [], idTypes = [], acceptedDocuments = DEFAULT_ACCEPTED_DOCUMENTS) => {
  const matchingDocuments = (documents || []).filter((document) => idTypes.some((type) => type.dsscId === document.type) && !isSupportingDocument(document.type, acceptedDocuments));
  const document = matchingDocuments.find(isDocumentEligible);
  return {
    eligible: idTypes.length === 0 || !!document,
    document,
    hasSupporting: (documents || []).some((item) => idTypes.some((type) => type.dsscId === item.type) && isSupportingDocument(item.type, acceptedDocuments)),
    rejected: matchingDocuments.some((item) => item.reviewStatus === DOCUMENT_REVIEW_STATUSES.rejected),
    expiredDocument: matchingDocuments.find((item) => getDocumentValidity(item) === DOCUMENT_VALIDITY_STATUSES.expired),
    pending: matchingDocuments.some((item) => item.reviewStatus === DOCUMENT_REVIEW_STATUSES.pending && isDocumentCurrent(item)),
  };
};

/** Derive account KYC flags from the effective catalogue, independently of upload visibility. */
export const getAccountDocumentFlags = (user, acceptedDocuments = DEFAULT_ACCEPTED_DOCUMENTS) => {
  const status = getPrimaryDocumentStatus(user.document, acceptedDocuments);
  return {
    kycPending: !status.eligible || !user.account || !Object.keys(user.account).length ||
      user.account.status === 256 || !!user.account.showClearKYCReject,
    kycExpired: status.allExpired,
    kycExpiredIncoming: status.expiringSoon,
  };
};
